import type { MergeLink } from 'shared-models/browser';
import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { MergeLink };

export type CreateMergeLinkInput = {
  tablePk: number;
  orderPk: number;
};

class MergeService {
  // Summary: Bulk-reassigns all orderlines from sourceOrderId into targetOrderId and closes source. 204 response.
  // Details: docs/function-library/web/services.md#merge-service
  async mergeOrderIntoOrder(
    tenantId: string,
    sourceOrderId: number,
    targetOrderId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/merge/tenant/${tenantId}/order/${sourceOrderId}/merge-into`,
        { targetOrderId },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to merge order'));
    }
  }

  // Summary: Loads all active merge links for the tenant (floor view chain labels).
  // Details: docs/function-library/web/services.md#merge-service
  async getActiveMergeLinks(tenantId: string, employeeSession: string): Promise<MergeLink[]> {
    try {
      const response = await authenticatedInstance.get<MergeLink[]>(
        `/merge/tenant/${tenantId}/links`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to load merge links'));
    }
  }

  // Summary: Batch-creates active merge links connecting each table to the merged order.
  // Details: docs/function-library/web/services.md#merge-service
  async createMergeLinks(
    tenantId: string,
    links: CreateMergeLinkInput[],
    employeeSession: string,
  ): Promise<MergeLink[]> {
    try {
      const response = await authenticatedInstance.post<MergeLink[]>(
        `/merge/tenant/${tenantId}/links`,
        { links },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to create merge links'));
    }
  }

  // Summary: Deactivates specific merge links by id (table split flow).
  // Details: docs/function-library/web/services.md#merge-service
  async deactivateMergeLinks(
    tenantId: string,
    linkIds: number[],
    orderId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.delete<void>(
        `/merge/tenant/${tenantId}/links`,
        {
          data: { linkIds, orderId },
          headers: { 'x-employee-session': employeeSession },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to deactivate merge links'));
    }
  }
}

export const mergeService = new MergeService();
